import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { debounce } from 'lodash'

import { TextField, CircularProgress, makeStyles } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { ExpandMore } from '@material-ui/icons'

import api from '~/api'
import track from '~/track'
import { useLiterals } from '~/components/Literals'

import { RowOption } from './RowOption'
import { ListboxComponent, renderGroup } from './ListboxComponent'
import { ImageProduct } from './ImageProduct'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  groupLabel: {
    fontSize: 16,
    color: '#000',
  },
  input: {
    '&::placeholder': {
      color: '#000',
      opacity: 1,
    },
  },
  listbox: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
    ...theme.gofind.scrollBar,
  },
}))

function getSlug(value) {
  return value
    ?.toLowerCase()
    ?.replace(/\s+/g, '-')
    ?.replace(/[^a-z0-9-]/g, '')
}

const handleSearchProducts = debounce((query, setLoading, setOptions, position) => {
  setLoading(true)

  const params = position.getParams()

  api
    .search({ ...params, name: query || '' })
    .then((result) => {
      setLoading(false)
      setOptions(result)
    })
    .catch(() => {
      setLoading(false)
      setOptions([])
    })
}, 1000) // Atraso de 1 segundos

export function SearchProductVirtualized({ product, position, config }) {
  const classes = useStyles()
  const history = useHistory()
  const { getLiteral } = useLiterals()
  const { listingPlaceholderStore = '' } = config

  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(null)
  const [inputValue, setInputValue] = useState('')

  const defaultOption = { gtin: 'all', name: 'Qualquer produto', imageurl: '' }

  const renderInputProps = (params) => {
    const { InputProps } = params

    return {
      ...InputProps,
      startAdornment: selected && <ImageProduct product={selected} dimensions={{ width: 40, height: 40 }} />,
      endAdornment: (
        <>
          {loading && <CircularProgress color="primary" size={16} style={{ marginRight: 4 }} />}
          {InputProps.endAdornment}
        </>
      ),
    }
  }

  const handleChangeOption = (_, item, reason) => {
    if (reason === 'blur') return

    if (reason === 'clear') {
      setOpen(true)
      setSelected(null)
      setInputValue('')

      return
    }

    if (reason === 'select-option' && item) {
      setSelected(item)

      history.push(`/produto/${getSlug(item?.name)}/${item?.gtin}`, {
        from: `${history.location.pathname}${history.location.search}`,
      })

      track('selecionarProduto', { category: 'menu', name: item?.name })
    }

    if (!item || item.gtin === 'all') {
      setSelected(null)
      history.push('/produto/todos')
    }
  }

  const handleOpenPopper = () => {
    setOpen(true)
  }

  const handleClosePopper = () => {
    setOpen(false)
  }

  useEffect(() => {
    handleSearchProducts(inputValue, setLoading, setOptions, position)
  }, [inputValue, position])

  useEffect(() => {
    if (product) setSelected(product)
  }, [product])

  return (
    <Autocomplete
      open={open}
      clearOnBlur={false}
      blurOnSelect
      autoComplete
      disableListWrap
      includeInputInList
      classes={{
        root: classes.root,
        input: classes.input,
        listbox: classes.listbox,
        groupLabel: classes.groupLabel,
      }}
      options={[defaultOption, ...options]}
      loading={loading}
      value={selected}
      defaultValue={product}
      renderGroup={renderGroup}
      groupBy={(_) => listingPlaceholderStore || 'Selecione um produto:'}
      onOpen={handleOpenPopper}
      onClose={handleClosePopper}
      onChange={handleChangeOption}
      openText={getLiteral('1612')}
      clearText={getLiteral('1260')}
      closeText={getLiteral('1251')}
      noOptionsText={getLiteral('1063')}
      popupIcon={<ExpandMore color="primary" />}
      filterOptions={(options, _) => options}
      getOptionLabel={(option) => option.name}
      getOptionSelected={(option, value) => option.gtin === value.gtin}
      ListboxComponent={ListboxComponent}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          color="primary"
          variant="outlined"
          onChange={(event) => setInputValue(event.target.value)}
          placeholder={listingPlaceholderStore || 'Buscar qualquer produto'}
          InputProps={renderInputProps(params)}
          inputProps={{
            ...params.inputProps,
            onKeyDown: (event) => {
              if (event.key === 'Enter') event.stopPropagation()
            },
          }}
        />
      )}
      renderOption={(option) => <RowOption option={option} />}
    />
  )
}
